// S-DM-006 / S-DM-007 — the single shared home for "how many matches did that
// search have" and for the `repolens:lastSearchCounts` localStorage envelope.
//
// Why a versioned envelope (S-DM-007): the pre-migration build wrote a bare
// `Record<string, number>` whose values were the *page size* (`nodes.length`),
// not the true hit count. Those values already sit in real browsers and will
// never self-correct, so the reader must be able to tell an old-shape entry
// from a new-shape one and discard the old one rather than display it.
//
// Every read and write of this key goes through this module — no raw
// getItem/setItem for `repolens:lastSearchCounts` anywhere else (AC5).

export const LAST_SEARCH_COUNTS_KEY = 'repolens:lastSearchCounts';

/** Schema version of the envelope this build writes and understands. */
export const LAST_SEARCH_COUNTS_VERSION = 2;

/** Where a stored count came from — the backend total, or the page length. */
export type CountSource = 'totalCount' | 'nodesLength';

export interface SearchCountEntry {
  count: number;
  source: CountSource;
}

export interface LastSearchCountsEnvelope {
  version: number;
  counts: Record<string, SearchCountEntry>;
}

export type SearchCounts = Record<string, SearchCountEntry>;

function isCountEntry(value: unknown): value is SearchCountEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as { count?: unknown; source?: unknown };
  return (
    typeof entry.count === 'number' &&
    Number.isFinite(entry.count) &&
    entry.count >= 0 &&
    (entry.source === 'totalCount' || entry.source === 'nodesLength')
  );
}

/**
 * Parse a raw localStorage string into counts.
 *
 * Discards — returning `{}` — anything that is not an envelope written by this
 * exact schema version: `null`, corrupt JSON, an array, the old unversioned
 * `Record<string, number>` shape, or an envelope from a *newer* build we cannot
 * interpret. Never throws (S-DM-007 AC1, AC3).
 */
export function parseLastSearchCounts(raw: string | null): SearchCounts {
  if (raw === null) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  const envelope = parsed as { version?: unknown; counts?: unknown };

  // No version marker => the pre-migration shape. Its values were page sizes,
  // so they are discarded rather than migrated: there is nothing in the old
  // record that lets us recover a true count.
  if (typeof envelope.version !== 'number') return {};

  // A future version may mean anything; discard rather than guess or throw.
  if (envelope.version !== LAST_SEARCH_COUNTS_VERSION) return {};

  if (typeof envelope.counts !== 'object' || envelope.counts === null || Array.isArray(envelope.counts)) {
    return {};
  }

  const out: SearchCounts = {};
  for (const [repo, entry] of Object.entries(envelope.counts as Record<string, unknown>)) {
    if (isCountEntry(entry)) out[repo] = entry;
  }
  return out;
}

/**
 * Read + migrate the stored counts. Safe when localStorage is unavailable.
 *
 * When a raw value existed but yielded nothing usable (pre-migration shape,
 * corrupt JSON, future version) the key is removed so the stale value cannot be
 * re-read by a later build, and so a failed `removeItem` cannot make it visible.
 */
export function readLastSearchCounts(): SearchCounts {
  let raw: string | null;
  try {
    raw = localStorage.getItem(LAST_SEARCH_COUNTS_KEY);
  } catch {
    return {};
  }

  const counts = parseLastSearchCounts(raw);

  if (raw !== null && Object.keys(counts).length === 0) {
    try {
      localStorage.removeItem(LAST_SEARCH_COUNTS_KEY);
    } catch {}
  }

  return counts;
}

/** Serialize counts into the versioned envelope this build writes. */
export function serializeLastSearchCounts(counts: SearchCounts): string {
  const envelope: LastSearchCountsEnvelope = {
    version: LAST_SEARCH_COUNTS_VERSION,
    counts,
  };
  return JSON.stringify(envelope);
}

/**
 * Record one repo's count, preserving the other repos' entries. Returns the
 * merged counts so callers can keep in-memory state in step with storage.
 * Swallows quota / private-browsing `setItem` failures.
 */
export function writeLastSearchCount(
  repo: string,
  count: number,
  source: CountSource,
): SearchCounts {
  const merged: SearchCounts = { ...readLastSearchCounts(), [repo]: { count, source } };
  try {
    localStorage.setItem(LAST_SEARCH_COUNTS_KEY, serializeLastSearchCounts(merged));
  } catch {}
  return merged;
}

/** Flatten to the plain `repo -> number` shape the repo list renders. */
export function toCountRecord(counts: SearchCounts): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [repo, entry] of Object.entries(counts)) out[repo] = entry.count;
  return out;
}
